import { Component } from "@theme/component";

/**
 * Timeline - An interactive chronological timeline with image slides and text content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement[]} slides - The slide elements
 * @property {HTMLElement[]} stops - The timeline stop buttons
 * @property {HTMLElement} heading - The heading element
 * @property {HTMLElement} body - The body element
 * @property {HTMLElement[]} dataEntries - JSON data script elements for each entry
 *
 * @extends {Component<Refs>}
 */
export class TimelineComponent extends Component {
  #current = 0;

  get total() {
    return (this.refs.slides ?? []).length;
  }

  /**
   * Navigate by delta (1 for next, -1 for previous)
   * @param {{ delta: number }} data
   * @param {Event} event
   */
  navigate(data, event) {
    event.preventDefault();
    this.#goTo((this.#current + data.delta + this.total) % this.total);
  }

  /**
   * Go to a specific slide by index
   * @param {{ index: number }} data
   * @param {Event} event
   */
  goToSlide(data, event) {
    event.preventDefault();
    this.#goTo(data.index);
  }

  #goTo(index) {
    if (index === this.#current) return;

    const { slides, stops, heading, body, dataEntries } = this.refs;

    for (const slide of slides ?? []) {
      slide.classList.toggle("timeline__slide--active", slide.dataset.slide === String(index));
    }

    for (const stop of stops ?? []) {
      stop.classList.toggle("timeline__stop--active", stop.dataset.index === String(index));
    }

    const dataEl = (dataEntries ?? []).find((el) => el.dataset.timelineData === String(index));

    if (dataEl && heading && body) {
      try {
        const data = JSON.parse(dataEl.textContent);
        heading.textContent = data.heading || "";
        body.innerHTML = data.body || "";
      } catch {
        // silently fail
      }
    }

    this.#current = index;
  }
}

if (!customElements.get("timeline-component")) {
  customElements.define("timeline-component", TimelineComponent);
}
